import { ball, body, coverBg, edge, fade, gloss, mix, n, rng, sector, shade, shadowInk, tint, warm, type ArtPal } from './kit';

/** Pot: a colourful wheel of many thin sectors with a pointer on top (right half). */
export function potCover(P: ArtPal, p: string): string {
  const cx = 440;
  const cy = 214;
  const R = 142;
  const top = P.dark ? mix(P.bg, P.orange, 0.08) : tint(P.orange, 0.9);
  const mid = P.dark ? mix(P.panel, P.purple, 0.08) : tint(P.gold, 0.92);
  const bg = coverBg(p, P, top, mid, P.bg, [cx, cy]);
  const cols = [P.teal, P.orange, P.blue, P.pink, P.gold, P.purple, P.green, P.sky, P.red];
  const k = 27;
  const gap = 0.012;
  // uneven sector sizes (stakes), deterministic
  const r = rng(7);
  const weights = Array.from({ length: k }, () => 0.5 + r() * 1.3);
  const total = weights.reduce((a, b) => a + b, 0);
  const slices: string[] = [];
  let acc = 0;
  weights.forEach((wt, i) => {
    const a0 = -Math.PI / 2 + (acc / total) * Math.PI * 2;
    acc += wt;
    const a1 = -Math.PI / 2 + (acc / total) * Math.PI * 2;
    const c = cols[i % cols.length] ?? P.teal;
    slices.push(`<path d="${sector(cx, cy, 44, R - 16, a0 + gap, a1 - gap)}" fill="${c}"/>`);
  });
  const rimEdge = P.dark ? shade(P.ink, 0.55) : shade(P.ink, 0.1);
  const rimC = P.dark ? mix(P.panel, P.ink, 0.22) : '#FFFFFF';

  // tick pegs on the rim
  const pegs: string[] = [];
  for (let i = 0; i < k; i++) {
    const a = -Math.PI / 2 + (i / k) * Math.PI * 2;
    pegs.push(`<circle cx="${n(cx + (R - 8) * Math.cos(a))}" cy="${n(cy + (R - 8) * Math.sin(a))}" r="2.6" fill="${P.dark ? tint(P.gold, 0.2) : P.gold}"/>`);
  }

  const defs =
    `<defs>${bg.defs}` +
    fade(`${p}sh`, shadowInk(P), P.dark ? 0.55 : 0.18) +
    body(`${p}rim`, rimC, 0.5, P.dark ? 0.45 : 0.14, 0.6, 1) +
    // spherical shading over the flat sectors (light top-left, shade bottom-right)
    `<radialGradient id="${p}vol" cx=".36" cy=".3" r=".78"><stop offset="0" stop-color="#FFFFFF" stop-opacity="${P.dark ? 0.28 : 0.34}"/><stop offset=".45" stop-color="#FFFFFF" stop-opacity="0"/><stop offset=".8" stop-color="#000000" stop-opacity="${P.dark ? 0.12 : 0.06}"/><stop offset="1" stop-color="#000000" stop-opacity="${P.dark ? 0.36 : 0.18}"/></radialGradient>` +
    ball(`${p}hub`, warm(P), 0.45, 0.4) +
    body(`${p}ptr`, P.orange, 0.4, 0.35, 0.5, 1) +
    `</defs>`;

  return (
    defs +
    bg.body +
    `<ellipse cx="${cx + 10}" cy="${cy + R + 16}" rx="${R * 0.95}" ry="20" fill="url(#${p}sh)"/>` +
    // rim
    `<circle cx="${cx}" cy="${cy}" r="${R}" fill="url(#${p}rim)" stroke="${rimEdge}" stroke-width="2"/>` +
    `<circle cx="${cx}" cy="${cy}" r="${R - 15}" fill="${P.dark ? P.bg : shade(P.bg, 0.08)}"/>` +
    slices.join('') +
    pegs.join('') +
    `<circle cx="${cx}" cy="${cy}" r="${R - 15}" fill="url(#${p}vol)"/>` +
    // hub
    `<circle cx="${cx}" cy="${cy}" r="46" fill="${P.dark ? P.panel : '#FFFFFF'}" stroke="${rimEdge}" stroke-width="2"/>` +
    `<circle cx="${cx}" cy="${cy}" r="30" fill="url(#${p}hub)" stroke="${edge(P.gold, P)}" stroke-width="2"/>` +
    gloss(cx - 10, cy - 12, 10, 5, -35, 0.6) +
    gloss(cx - 70, cy - 92, 48, 10, -36, P.dark ? 0.16 : 0.4) +
    // pointer on top (teardrop pointing down into the wheel)
    `<g transform="translate(${cx} ${cy - R - 6})">` +
    `<ellipse cx="3" cy="40" rx="14" ry="4" fill="url(#${p}sh)"/>` +
    `<path d="M0 34C-8 22-22 12-22 -2C-22 -16-12 -24 0 -24C12 -24 22 -16 22 -2C22 12 8 22 0 34Z" fill="url(#${p}ptr)" stroke="${edge(P.orange, P)}" stroke-width="2" stroke-linejoin="round"/>` +
    `<circle cx="0" cy="-3" r="7" fill="${P.dark ? P.bg : '#FFFFFF'}" fill-opacity=".85"/>` +
    gloss(-9, -13, 6, 3, -35, 0.6) +
    `</g>`
  );
}
